export interface IVirus {
  id: string;
  name: string;
  abbreviation: string;
  description: VirusInfo;
}

export interface VirusInfo {
  about: string;
  epidemiology: string;
  targets: string[];
}

export const VIRUSES: IVirus[] = [
  {
    id: "DENV",
    name: "Dengue Virus",
    abbreviation: "DENV",
    description: {
      about:
        "Dengue virus (DENV) is a flavivirus with a positive-sense RNA genome encoding three structural proteins and seven non-structural proteins. NS5 possesses RNA-dependent RNA polymerase (RdRp) and methyltransferase activities. NS3 functions as a protease and helicase. DENV exists as four serotypes. Secondary infection with a different serotype risks antibody-dependent enhancement (ADE), where non-neutralizing antibodies worsen disease.",
      epidemiology:
        "100-400 million annual infections across >100 countries, 22,000 deaths yearly. Transmitted by Aedes aegypti. Over 5 million cases in 2023; 2024 saw cases 238% higher than previous year in Americas.",
      targets: [
        "NS5 RdRp: Compound 3md inhibits DENV-2 (EC₅₀ 3.63 μM). Hit compound 3 disrupts NS3-NS5 interaction, active against all serotypes.",
        "Host-targeted: Resomelagon (Phase II, Brazil); DEN-HOST trial evaluating baricitinib, dexamethasone, N-acetylcysteine.",
        "Repurposed: Doxycycline with Carica papaya (reduced hospital stay); eltrombopag (increases platelets).",
        "Natural products: Cordycepin, andrographolide, fucoidan, berberine.",
        "Novel: EYU688 (Novartis) in Phase II across six countries.",
      ],
    },
  },
  {
    id: "CHIKV",
    name: "Chikungunya Virus",
    abbreviation: "CHIKV",
    description: {
      about:
        "Chikungunya virus (CHIKV) is an alphavirus with a positive-sense RNA genome encoding nsP1-4 and structural proteins E1-E2. nsP4 is the RdRp; nsP1 is the methyltransferase. E2 mediates receptor binding; E1 drives membrane fusion. The E1-A226V mutation enhanced Aedes albopictus transmission.",
      epidemiology:
        "119 countries report local transmission; ~35 million annual infections. Chronic arthralgia accounts for 54% of disease burden, affecting ages 40-60.",
      targets: [
        "nsP4 RdRp: VV261 (oral 4'-fluorouridine prodrug) potently inhibits CHIKV, reduces joint viral titers, wide therapeutic window.",
        "nsP1 methyltransferase: Berbamine, venetoclax, ponatinib (IC₅₀ <100 nM) from structure-based screening; also active against Sindbis virus.",
        "Repurposed: Efavirenz (HIV drug) inhibits replication by 99% in vitro, reduces viral load in mice.",
      ],
    },
  },
  {
    id: "MERS-CoV",
    name: "Middle East Respiratory Syndrome Coronavirus",
    abbreviation: "MERS-CoV",
    description: {
      about:
        "MERS-CoV is a betacoronavirus with a 30 kb genome encoding 16 non-structural proteins and spike (S), envelope, membrane, and nucleocapsid proteins. Spike binds DPP4 receptor. Key targets: RdRp (nsp12), 3CLpro (nsp5), helicase (nsp13).",
      epidemiology:
        "2,647 confirmed cases, 959 deaths since 2012 (36% CFR). Cases in 27 countries; Arabian Peninsula accounts for majority. Only 11 cases in 2025; none in early 2026.",
      targets: [
        "RdRp: Remdesivir inhibits through delayed chain termination.",
        "3CLpro: Kihadanin B, robustaflavone (binding energies -9.8, -9.4 kcal/mol) from in silico screening.",
        "Fusion: Stapled peptide MjHKU4r-HR2P10 blocks six-helix bundle formation (IC₅₀ 3.56-8.29 nM).",
        "Host: Nafamostat (TMPRSS2 inhibitor); interferon-β1b (innate immune enhancement); lopinavir/ritonavir combination.",
      ],
    },
  },
  {
    id: "SARS-CoV",
    name: "Severe Acute Respiratory Syndrome Coronavirus",
    abbreviation: "SARS-CoV",
    description: {
      about:
        "SARS-CoV is a betacoronavirus with a 29.7 kb genome encoding 16 non-structural proteins and spike, envelope, membrane, and nucleocapsid proteins. Spike binds ACE2 receptor. Key targets: RdRp (nsp12), 3CLpro (nsp5), PLpro (nsp3), helicase (nsp13).",
      epidemiology:
        "8,096 confirmed cases, 774 deaths (9.6% CFR) across 29 countries in 2002-2003. No human cases since 2004.",
      targets: [
        "RdRp: Remdesivir inhibits through delayed chain termination; directly informed COVID-19 therapeutics.",
        "3CLpro: Structure-based design led to peptidomimetic inhibitors covalently modifying Cys145, directly informing nirmatrelvir development.",
        "PLpro: GRL0617 lead compound; resistance mutations emerged.",
        "Entry: Neutralizing antibodies; soluble ACE2 decoys; HR2-derived fusion inhibitors.",
        "Host: Camostat, nafamostat (TMPRSS2 inhibitors); imatinib (kinase inhibitor).",
      ],
    },
  },
  {
    id: "CCHFV",
    name: "Crimean-Congo Hemorrhagic Fever Virus",
    abbreviation: "CCHFV",
    description: {
      about:
        "CCHFV is an orthonairovirus with tripartite negative-sense RNA genome (S, M, L segments). L segment encodes RdRp and OTU protease that blocks interferon responses. M segment encodes Gn and Gc glycoproteins. Transmitted by Hyalomma ticks.",
      epidemiology:
        "Endemic in >30 countries; 3 billion people at risk; 10,000-15,000 cases annually. CFR 5-40% (average 30%). Transmission via tick bites, contact with infected livestock, nosocomial exposure.",
      targets: [
        "RdRp: Ribavirin (controversial efficacy); favipiravir (encouraging in animal models).",
        "OTU protease: Paromomycin (in silico, XP GScore -11.156 kcal/mol).",
        "G-quadruplex RNA structure: Cepharanthine stabilizes G4 structure, blocks host DDX60 unwinding; 88× more potent than ribavirin in preclinical models.",
      ],
    },
  },
  {
    id: "EBOV",
    name: "Zaire Ebolavirus",
    abbreviation: "EBOV",
    description: {
      about:
        "EBOV is a filovirus with negative-sense RNA genome encoding seven proteins. Glycoprotein (GP) mediates entry; VP35 and VP24 antagonize interferon responses; VP40 drives budding; L protein is RdRp.",
      epidemiology:
        "2014-2016 West Africa outbreak: 28,600 cases, 11,300 deaths. 2018-2020 DRC outbreak: 3,470 cases. CFR 30-90%. Transmitted through direct contact with bodily fluids; persists in semen.",
      targets: [
        "Glycoprotein: Inmazeb (atoltivimab, maftivimab, odesivimab) and Ansuvimab (Ebanga) — FDA-approved monoclonal antibodies.",
        "L protein (RdRp): Galidesivir (BCX4430) preclinical.",
        "VP24: Galidesivir analogs CID 117698807 and CID 117712809 (ΔGbinding -36.7, -53.4 kcal/mol) from computational screening.",
        "VP35: Interferon inhibitory domain structurally characterized; small molecule inhibitors under investigation.",
      ],
    },
  },
  {
    id: "IV",
    name: "Influenza Virus",
    abbreviation: "IV",
    description: {
      about:
        "Influenza virus is an orthomyxovirus with eight-segment negative-sense RNA genome. Hemagglutinin (HA) mediates entry; neuraminidase (NA) facilitates release; M2 ion channel mediates uncoating; RdRp complex (PB1, PB2, PA) performs cap-snatching and replication.",
      epidemiology:
        "1 billion annual infections; 290,000-650,000 respiratory deaths yearly. Influenza A and B cause seasonal epidemics.",
      targets: [
        "Neuraminidase: Oseltamivir, zanamivir, peramivir (approved). CD388 (NA inhibitor-Fc conjugate) in Phase 3; Breakthrough Therapy Designation.",
        "PA endonuclease: Baloxavir marboxil (approved).",
        "RdRp: Favipiravir (approved in Japan).",
        "Nucleoprotein: Lead compounds disrupting NP-NP tail loop interaction (preclinical).",
        "HA: Nafamostat, camostat (protease inhibitors; preclinical); stachyflin (fusion inhibitor; preclinical).",
        "M2: Amantadine, rimantadine (not recommended due to resistance).",
      ],
    },
  },
  {
    id: "JUNV",
    name: "Junin Virus",
    abbreviation: "JUNV",
    description: {
      about:
        "JUNV is a mammarenavirus with bisegmented negative-sense RNA genome (S, L segments). S encodes nucleocapsid (NP) and glycoprotein precursor (GPC → GP1, GP2). L encodes RdRp and Z protein (matrix, drives budding). Rodent-borne.",
      epidemiology:
        "Argentine hemorrhagic fever (AHF) endemic to Argentine Pampas. CFR 20-30% untreated. Geographic range expanded from 15,000 to 150,000 km².",
      targets: [
        "RdRp: Nucleoside analogs (Gilead patent US 11,007,208).",
        "Host NMT: NMT inhibitors block Z protein myristoylation; complete inhibition in vitro.",
        "Pandemic Response Box hits: Verdinexor (XPO1 inhibitor, blocks nuclear export); WO 2006118607 A2 (DHODH inhibitor, depletes pyrimidines); Ro-24-7429 (RUNX inhibitor). Verdinexor + DHODHi exhibit strong synergy.",
        "Host factors: RBM15, ZC3H13, ABHD16A, PRSS23 (shared with Ebola virus; target identification).",
      ],
    },
  },
  {
    id: "LASV",
    name: "Lassa Virus",
    abbreviation: "LASV",
    description: {
      about:
        "LASV is a mammarenavirus with bisegmented negative-sense RNA genome. S segment encodes NP and GPC (→ GP1, GP2); L segment encodes RdRp and Z protein. Reservoir: multimammate rat (Mastomys natalensis).",
      epidemiology:
        "100,000-300,000 infections, 5,000 deaths annually in West Africa. CFR 1% overall, 15% hospitalized. ECG alterations in 45% of patients (cardiac involvement).",
      targets: [
        "Glycoprotein: ARN-75039 (entry inhibitor, Phase I); DEFENDER project (neutralizing nanobodies); evodiamine derivatives (in silico).",
        "Nucleoprotein: Phelligridin-I, Phellibaumin A (fungal metabolites, docking scores -10.75, -7.66 kcal/mol).",
        "RdRp: Favipiravir (Phase II completed in Nigeria).",
        "Host: Flunarizine (calcium channel blocker) inhibits vRNP activity and fusion; U-73122 (phospholipase C inhibitor) impairs virion infectivity.",
        "Monoclonal antibodies: Arevirumab-3 (8.9F, 12.1F, 37.2D) protected 100% of macaques against lineage II/III, but only 40% against lineage VII.",
        "Off-label: Ribavirin (limited efficacy).",
      ],
    },
  },
  {
    id: "MARV",
    name: "Marburg Virus",
    abbreviation: "MARV",
    description: {
      about:
        "MARV is a filovirus with non-segmented negative-sense RNA genome encoding seven proteins. Glycoprotein (GP) mediates entry via NPC1; VP35 is interferon antagonist; VP40 drives budding; L protein is RdRp.",
      epidemiology:
        "Angola 2004-2005 outbreak: 422 cases, 356 deaths (84% CFR). Rwanda 2024: 66 cases, 15 deaths. Tanzania 2025: 2 confirmed, 8 probable cases (all fatal). CFR 23-90%.",
      targets: [
        "RdRp: Favipiravir (83% survival in macaques); remdesivir (efficacious in NHPs); galidesivir, obeldesivir.",
        "Glycoprotein: Monoclonal antibodies MBP091, MR-78-N, MR82-N, MR191-N, MR186-YTE. MBP091 + remdesivir used in Rwanda outbreak.",
        "VP35: Estradiol benzoate, paliperidone (in silico; validation needed).",
        "Nucleic acid: siRNA, phosphorodiamidate morpholino oligomers (PMOs) in preclinical.",
        "Host: Interferon-β (prolonged survival in NHPs); tilorone, quinacrine (further study needed).",
      ],
    },
  },
  {
    id: "MPXV",
    name: "Monkeypox Virus",
    abbreviation: "MPXV",
    description: {
      about:
        "MPXV is an orthopoxvirus with dsDNA genome (~197 kb). Produces IMV (host-to-host) and EEV (cell-to-cell spread). Key targets: F13 (VP37, EEV formation), DNA polymerase (E9L), DdRp (A6R subunit). Clade I (CFR up to 10%); Clade II (CFR ~1-3%).",
      epidemiology:
        "Global outbreak since 2022: ~110 countries affected. HIV-positive individuals disproportionately affected (up to 49% of cases). PHEIC declared in 2022 and again in 2024.",
      targets: [
        "F13 (VP37): Tecovirimat (TPOXX) (FDA-approved; Phase 3 trials failed to meet primary endpoints; resistance mutations emerged).",
        "DNA polymerase (E9L): Cidofovir (IV, nephrotoxic); Brincidofovir (Tembexa) (oral prodrug, FDA-approved).",
        "Host eEF1A: Plitidepsin (preclinical; reduces lung viral loads).",
        "DdRp (A6R): Nilotinib, conivaptan, ponatinib (in silico; binding energy -7.5 kcal/mol).",
        "VP37 (AI screen): Lumacaftor (validation ongoing).",
        "Passive: Vaccinia Immune Globulin Intravenous (VIGIV).",
      ],
    },
  },
  {
    id: "NiV",
    name: "Nipah Virus",
    abbreviation: "NiV",
    description: {
      about:
        "NiV is a henipavirus with negative-sense RNA genome encoding N, P, M, F, G, and L proteins. G glycoprotein binds ephrin-B2/B3 receptors; F protein mediates membrane fusion. P gene encodes V, W, C (interferon antagonists). L protein is RdRp.",
      epidemiology:
        "First identified in 1998-1999 Malaysia outbreak. Since then, annual outbreaks in Bangladesh; sporadic outbreaks in India (most recent January 2026 in West Bengal). CFR 40-75%. Transmission via bats, pigs, human-to-human, and nosocomial.",
      targets: [
        "RdRp (L protein): VV261 (oral nucleoside analog) in hamster model: 66.7% survival, reduced viral loads in lung, spleen, brain. Remdesivir (100% survival in NHPs). Favipiravir, ALS-8112 (in vitro).",
        "Glycoprotein (G): m102.4 (human monoclonal antibody) Phase 1 completed (safe, well-tolerated); emergency use during outbreaks. hu1F5 (preclinical).",
        "Fusion protein (F): Fusion inhibitory lipopeptides (preclinical; CNS activity). Griffithsin (lectin; blocks fusion).",
        "FDA screen (2,703 drugs): Bortezomib, harringtonine, homoharringtonine, ixazomib citrate, lanatoside C (IC₅₀ ≤0.45 µM; post-entry targets).",
        "Host: Poly(I)-poly(C12U) (interferon inducer; 5/6 survival in hamsters); 6-aza-uridine, pyrazofurin (OMP-decarboxylase inhibitors; delay mortality).",
      ],
    },
  },
  {
    id: "RVFV",
    name: "Rift Valley Fever Virus",
    abbreviation: "RVFV",
    description: {
      about:
        "RVFV is a phlebovirus with tripartite negative-sense RNA genome (L, M, S segments). L segment encodes RdRp (endonuclease, RdRp, cap-binding domains). M segment encodes Gn and Gc glycoproteins. S segment (ambisense) encodes nucleoprotein (N) and non-structural protein (NSs), a major interferon antagonist. Transmitted by Aedes mosquitoes with transovarial persistence.",
      epidemiology:
        "Endemic in sub-Saharan Africa; outbreaks in Egypt (1977, >200,000 cases), Saudi Arabia and Yemen (2000), Turkey (2005), China (2016). Estimated >1 billion people at risk. Human prevalence increased from 1.92% pre-1977 to 5.24% post-2000. CFR ~2% overall; up to 50% in hemorrhagic form.",
      targets: [
        "RdRp (L protein): 4′-Fluorouridine (4′-FlU, EIDD-2749) (EC₉₀ 3.5 μM; 18-70× more potent than ribavirin/favipiravir); favipiravir (IC₅₀ 37 μM); 2′-Fluoro-2′-deoxycytidine (2′-FdC) (IC₅₀ 3.0 μM); EIDD-1931 (active molnupiravir, IC₅₀ 2.0 μM); galidesivir (preclinical). Resistance mutations mapped (P187A, Q191L, L289F, T296I).",
        "Host PI3K-Akt pathway: CNX-1351 identified via single-cycle viral replicon particle system; broad-spectrum potential.",
      ],
    },
  },
  {
    id: "SFTSV",
    name: "Severe Fever with Thrombocytopenia Syndrome Virus",
    abbreviation: "SFTSV",
    description: {
      about:
        "SFTSV (Dabie bandavirus) is a bandavirus with tripartite negative-sense RNA genome (L, M, S segments). L encodes RdRp; M encodes Gn and Gc glycoproteins (Gn binds NMMHC-IIA receptor); S (ambisense) encodes nucleoprotein (NP) and non-structural protein (NSs), a major interferon antagonist. Transmitted by Haemaphysalis longicornis ticks.",
      epidemiology:
        "30,388 cases in China, Japan, South Korea (2011-2023). CFR 10-30%. South Korea highest incidence (3.42 per million). Per-case direct cost: $5,813 (China), $4,255 (Japan), $2,981 (South Korea). WHO priority pathogen since 2017.",
      targets: [
        "RdRp (L protein): Favipiravir (EC₅₀ 6.0 μM; RCT reduced 28-day mortality from 18.3% to 9.5%); VV261 (EC₅₀ 0.89 μM; 100% survival in mice); 4′-Fluorouridine (EC₅₀ 5.5 μM). Ribavirin (no significant mortality benefit).",
        "Glycoproteins (Gn/Gc): S2A5, SNB02 monoclonal antibodies; SNB02 nanoantibody in clinical trials.",
        "Host calcium channels: Nifedipine (retrospective studies; RCT needed).",
        "Host metabolism: Metformin (observational; validation needed).",
        "Synergistic: Vitamin D derivatives + favipiravir (preclinical).",
      ],
    },
  },
  {
    id: "VARV",
    name: "Smallpox Virus",
    abbreviation: "VARV",
    description: {
      about:
        "Smallpox virus (variola virus) is an orthopoxvirus with linear dsDNA genome (~200 kb). Key targets: VP37 envelope protein (F13L) for EEV formation; DNA polymerase (E9L). Replicates entirely in cytoplasm. Exclusively human host; eradicated in 1980.",
      epidemiology:
        "Eradicated; no natural cases since 1977. Historically killed up to 300 million people in 20th century. CFR 10-30% (up to 80% in hemorrhagic form). Last fatal case: 1978 laboratory infection.",
      targets: [
        "VP37 (F13L): Tecovirimat (TPOXX, ST-246) (FDA-approved under Animal Rule; oral/IV).",
        "DNA polymerase (E9L): Cidofovir (IV, nephrotoxic); Brincidofovir (Tembexa, CMX001) (oral prodrug, reduced nephrotoxicity; FDA-approved).",
        "Passive immunization: Vaccinia Immune Globulin Intravenous (VIGIV).",
        "Emerging: NIOCH-14 (Russia); ST-357 (USA) in development.",
      ],
    },
  },
  {
    id: "SUDV",
    name: "Sudan Virus",
    abbreviation: "SUDV",
    description: {
      about:
        "Sudan virus (SUDV) is an orthoebolavirus with non-segmented negative-sense RNA genome encoding seven proteins. Glycoprotein (GP) mediates entry via NPC1; VP40 drives budding; VP35 and VP24 antagonize interferon; L protein is RdRp. GP shares structural homology with EBOV but binds NPC1 ~9× more strongly. No cross-protection with EBOV.",
      epidemiology:
        "First identified in 1976. Nine outbreaks since, with CFR 41-70%. Uganda 2022-2023: 142 cases, 55 deaths (39% CFR). Uganda January 2025: 14 cases, 4 deaths. No approved vaccines or treatments.",
      targets: [
        "Glycoprotein (GP): MBP134 (pan-ebolavirus monoclonal antibody cocktail; emergency doses produced for Uganda outbreak). FVM04 + CA45 cocktail (targets RBS and fusion loop; 100% protection in macaques with delayed treatment day 5). BA2-1A10 (bispecific nanobody targeting GP1 base and GP2 fusion loop; broad-spectrum protection in rodents). 16F6 (monoclonal antibody; inhibits fusion; structure resolved at 3.35 Å).",
        "VP40: Antiviral peptides (e.g., A10_M) identified in silico (binding energies -252 to -145 kcal/mol); validation needed.",
        "RdRp (L protein): Galidesivir (BCX4430) (in vitro activity).",
        "siRNA: TKM-Ebola showed protection in NHPs; delivery challenges.",
      ],
    },
  },
  {
    id: "TBEV",
    name: "Tick-Borne Encephalitis Virus",
    abbreviation: "TBEV",
    description: {
      about:
        "TBEV is a flavivirus with positive-sense RNA genome encoding structural proteins (C, prM, E) and non-structural proteins (NS1-NS5). Envelope (E) protein domain III (DIII) mediates receptor binding. NS5 possesses RdRp and methyltransferase activities; NS3 is protease/helicase. Three subtypes: European (CFR 0.5-2%), Siberian, Far Eastern (CFR up to 35%).",
      epidemiology:
        "10,000-12,000 cases reported annually; 400% increase over 30 years in Europe. Endemic across Europe and Asia. Seroprevalence 1-2.75% in endemic areas. Transmitted by Ixodes ticks; also foodborne via unpasteurized dairy.",
      targets: [
        "RdRp (NS5): Bemnifosbuvir (potent in neural organoids, well-tolerated). Remdesivir (active but cytotoxic in neural organoids). 4′-Fluorouridine, VV261, favipiravir, galidesivir under investigation.",
        "E protein DIII: TA3 nanobody-dendrimer conjugate (TA3-DNs) (KD 6.5 × 10⁻⁸ M) functionalized with Angiopep-2 for BBB penetration; neutralizes TBEV in vitro BBB model.",
        "Monoclonal antibodies: Human/chimeric antibodies targeting E protein (preclinical).",
        "Host (BBB delivery): Dendrimer nanomicelles, gold nanoparticles for CNS drug delivery.",
      ],
    },
  },
  {
    id: "VACV",
    name: "Vaccinia Virus",
    abbreviation: "VACV",
    description: {
      about:
        "Vaccinia virus (VACV) is an orthopoxvirus with linear dsDNA genome (~200 kb, >200 ORFs). Replicates entirely in cytoplasm. Produces multiple infectious forms: IMV (host-to-host) and EEV (cell-to-cell spread). Key targets: F13 (VP37, EEV formation); D9 decapping enzyme (removes caps from host/viral RNA for immune evasion); F10 kinase (virion morphogenesis); H1 phosphatase (virion maturation).",
      epidemiology:
        "Laboratory-adapted virus; used as smallpox vaccine. Emerging zoonotic pathogen in Brazil causing bovine vaccinia (dairy workers). ~6.1% seroprevalence in unvaccinated endemic areas. 2022 mpox outbreak renewed orthopoxvirus focus.",
      targets: [
        "F13 (VP37): Tecovirimat (TPOXX) (FDA-approved for smallpox; resistance reported in mpox patients).",
        "D9 decapping enzyme: m7GpppCH2p (IC₅₀ 0.08 μM); seliciclib (IC₅₀ 0.57 μM); CP-100356 (IC₅₀ 2.7 μM; cocrystal structure solved). D9-specific, no inhibition of human Dcp1/2.",
        "Host RhoA pathway: Narciclasine (natural alkaloid) activates RhoA, counteracting viral F11L-mediated suppression; blocks EEV formation at 40-160 nM.",
        "F10 kinase, H1 phosphatase: Target identification stage.",
      ],
    },
  },
  {
    id: "VEEV",
    name: "Venezuelan Equine Encephalitis Virus",
    abbreviation: "VEEV",
    description: {
      about:
        "VEEV is an alphavirus with positive-sense RNA genome encoding nsP1-4 and structural proteins. nsP4 is RdRp; nsP1 is methyltransferase; nsP2 has helicase/protease activities; E2 glycoprotein mediates receptor binding. Epizootic strains (IAB, IC) emerge from enzootic strains (ID, IE, IF) through mutations in E2 and RNA structures.",
      epidemiology:
        "Endemic in Latin America; periodic epizootics affecting equines and humans. Up to 10% of dengue burden in neotropical cities (tens-of-thousands of cases). Encephalitis in ~5% of infected; CFR 10-25% in encephalitic cases. Biodefense concern due to aerosol infectivity.",
      targets: [
        "nsP4 RdRp: 4′-Fluorouridine (4′-FlU) (submicromolar potency; resistance mutations P187A, Q191L, L289F, T296I but treatment still effective). BDGR-354 (oral; 100% protection in mice; NIH-funded ferret model ongoing). Molnupiravir (active in animal models).",
        "Host RNA structures: Novel RNA structures in E1 gene alter replication in macrophages; host RNA-binding proteins identified as potential targets.",
        "Vaccine: MVA-BN® WEV (MVA-vectored against VEEV/EEEV/WEEV) in Phase I (immunogenic, well-tolerated).",
      ],
    },
  },
  {
    id: "WNV",
    name: "West Nile Virus",
    abbreviation: "WNV",
    description: {
      about:
        "WNV is a flavivirus with positive-sense RNA genome encoding structural (C, prM, E) and non-structural (NS1-NS5) proteins. Envelope protein domain III (DIII) mediates receptor binding. NS5 possesses RdRp and methyltransferase activities; NS3 is protease/helicase. Transmitted by Culex mosquitoes; birds are reservoir hosts.",
      epidemiology:
        "Most widespread arbovirus globally. Causes the highest number of arboviral neurological disease in North America. 2024: 491 confirmed US cases, 342 neuroinvasive. Global seroprevalence 3.6% (North America 18.8%, Africa 4.8%). Largest European outbreak in 2022 (1,340 cases, 104 deaths).",
      targets: [
        "NS5 methyltransferase: Doravirine (HIV drug) binds KDKE motif and SAH site; most favorable among tested drugs in silico (molecular dynamics, MM-GBSA).",
        "NS5 RdRp: Remdesivir, rilpivirine (reported WNV RdRp inhibition; in silico).",
        "E protein DIII: Cyclic peptides (CTKTDVHFC, CIHSSTRAC, CTYENHRTC, CLAQSHPLC) in vitro neutralizing. Peptide P9 (CDVIALLACHLNT) reduces viremia and brain viral loads, crosses BBB. hE16 monoclonal antibody (80-88% survival in hamsters with delayed treatment).",
        "Host: Atorvastatin, ezetimibe (disrupt viral assembly); montelukast, doxycycline (anti-inflammatory).",
      ],
    },
  },
  {
    id: "YFV",
    name: "Yellow Fever Virus",
    abbreviation: "YFV",
    description: {
      about:
        "YFV is the prototypic flavivirus with positive-sense RNA genome encoding structural (C, prM, E) and non-structural (NS1-NS5) proteins. NS5 possesses RdRp and methyltransferase activities; NS2B-NS3 protease processes polyprotein; NS4B is essential for replication organelle morphogenesis. Transmitted by Aedes (Africa) and Haemagogus (South America) mosquitoes.",
      epidemiology:
        "1.7 million infections, 29,000-60,000 deaths annually. Endemic in 47 countries in Africa and South America. CFR up to 50% in severe cases. No approved antiviral drugs.",
      targets: [
        "NS5 RdRp: 7-deaza-7-fluoro-2′-C-methyladenosine (DFA) (EC₅₀ 0.9-5.5 μM; pan-flavivirus). Tiratricol (TRIAC) (EC₅₀ 2.07 μM; approved for thyroid disease; broad-spectrum flavivirus activity). Rifapentine (IC₅₀ 0.5 μM; dual entry + RdRp inhibition; superior to sofosbuvir in mice). 2-FBU (sub-micromolar potency). Sofosbuvir (active in vitro; limited in vivo efficacy).",
        "NS4B: Acetic acid benzodiazepine (BDAA) binds NS4B within minutes, disrupting replication organelles, exposing dsRNA, activating RIG-I-like receptors, and accelerating apoptosis (dual mechanism). Resistance mapped to NS4B P219.",
        "NS2B-NS3 protease: GA-Hecate peptide (IC₅₀ 32 nM for ZIKV; active against YFV protease). Computational screening (machine learning) for allosteric inhibitors.",
        'E protein: Monoclonal antibody 5A binds both pre- and post-fusion states ("double lock"), blocking attachment and fusion.',
      ],
    },
  },
  {
    id: "ZIKV",
    name: "Zika Virus",
    abbreviation: "ZIKV",
    description: {
      about:
        "ZIKV is a flavivirus with positive-sense RNA genome encoding structural (C, prM, E) and non-structural (NS1-NS5) proteins. Envelope protein (domains DI, DII, DIII) mediates entry via multiple receptors (ITGB4, GRP78, αvβ5). NS5 uniquely suppresses neurodevelopmental genes through nuclear localization. NS2B-NS3 protease processes polyprotein. Transmitted by Aedes mosquitoes, sexually, and vertically.",
      epidemiology:
        "2015-2016 Americas outbreak: ~1.62 million infections in Brazil alone. Currently circulating in 89 countries. Linked to congenital Zika syndrome (microcephaly) and Guillain-Barré syndrome. WHO priority pathogen.",
      targets: [
        "NS2B-NS3 protease: IRBM allosteric inhibitor (published in Nature Communications 2026) binds novel allosteric site; potent in biochemical/cellular assays; favorable safety; potential pan-flavivirus activity.",
        "NS5 RdRp: Tyzivumab (Phase I, NCT03443830). BCX4430 (Galidesivir) (Phase I, NCT02319772). Natural compounds L04, L05 (in silico, binding energies -8.00 to -10.59 kcal/mol).",
        "Host Raf kinases: Dabrafenib (100-fold titer reduction; blocks RNA replication). Regorafenib (10-fold reduction; blocks egress, inhibits NS1 secretion). Distinct mechanisms suggest combination potential.",
        "Host receptors: Anti-ITGB4 antibodies (protects placenta/embryos in mice); anti-GRP78 antibodies (50-70% entry inhibition); αvβ5-blocking antibodies (inhibits internalization in neural stem cells).",
        "Delivery: Nano-drug delivery systems for placental/CNS targeting.",
        "Vaccines: Multiple candidates in Phase I/II (inactivated, DNA, mRNA, viral vectored).",
      ],
    },
  },
];
